import { useEffect, useState } from "react";
import { subscribeToChanges } from "./data.ts";

/**
 * Keeps a screen current with the pipeline.
 *
 * The console is a window onto something that moves on its own, so a stale
 * screen is worse than no screen. Realtime is the fast path; a slow interval
 * is the backstop, exactly as in the worker itself. Returns whether the live
 * connection is actually up, so the UI can say so honestly rather than showing
 * a decorative "live" dot that means nothing.
 */
export function useLiveRefresh(refresh: () => void, fallbackMs = 60_000): boolean {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    try {
      unsubscribe = subscribeToChanges(refresh);
      setConnected(true);
    } catch {
      setConnected(false);
    }

    // Backstop: if the subscription silently dies, the screen still updates.
    const timer = setInterval(refresh, fallbackMs);

    // Coming back to the tab should show current data immediately.
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      unsubscribe?.();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      setConnected(false);
    };
  }, [refresh, fallbackMs]);

  return connected;
}
